import { authenticationMethods, required, userSchemaAttributes } from 'client/util/Constants/ApplicationConstants';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notify } from 'client/util';
import Constants, { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import ScimTestError from 'client/applications/scim/ScimTestError';
import Vue from 'vue';
import navigationTools from 'client/util/PageNavigationUtil';

const { application } = ItemTypeConsts;
const applicationModel = StoreModelMap[application];
const { success, error } = Constants;

export const idmExcludedApps = [
  'office365',
  'google',
  'aws',
];

export const SCIMPanelEventManager = new Vue();

const { OAUTH2 } = authenticationMethods;

export default {
  computed: {
    ...mapState(applicationModel, {
      authMethod: 'authenticationMethod',
      authState: 'scimAuthState',
      customIdmMappingAttributes: 'customIdmMappingAttributes',
      genericOldData: 'scimGenericOldData',
      idField: 'id',
      isGroupsEnabled: 'scimGroupsEnabled',
      isGroupsSupported: 'scimGroupsSupport',
      isScimActive: 'isScimActive',
      isTestConnectionPassed: 'scimIsTestConnectionPassed',
      isIdmOauthConfigured: 'isIdmOauthConfigured',
      isPrebuild: state => !!state.item.provision,
      needsCredentials: 'scimNeedsCredentials',
      needsSimpleToken: 'idmNeedsSimpleToken',
      redirectLocation: 'scimRedirectLocation',
      scimConnectionType: 'scimConnectionType',
      scimVersion: 'scimVersion',
      testConfigErrors: 'scimTestConfigErrors',
      testConnectionErrors: 'scimTestConnectionErrors',
      translationRules: 'scimTranslationRules',
      vanityUrl: 'scimVanityUrl',
    }),

    ...mapGetters(applicationModel, {
      areCredentialsChanged: 'areScimCredentialsChanged',
      didCustomApiConfigChanged: 'didCustomApiConfigChanged',
      didOauthConfigChanged: 'didOauthConfigChanged',
      isGenericConnectorChanged: 'isScimGenericConnectorChanged',
      isGroupsEnabledChanged: 'isScimGroupsEnabledChanged',
      isNew: 'isItemBeingCreated',
    }),

    connectionErrorsList() {
      return this.testConnectionErrors
        .map(failure => new ScimTestError(failure.errorType, failure.message));
    },

    configurationErrorsList() {
      return this.testConfigErrors
        .map(err => new ScimTestError(err.errorType, err.message));
    },

    /**
     * Returns an array with all optional user schema attributes for Custom REST API
     */
    getOptionalAttributes() {
      return userSchemaAttributes.filter(attribute => attribute.category !== required);
    },

    isOAuth2() {
      return this.authMethod === OAUTH2;
    },

    /**
     * TO DO:
     * This function should be deleted once `[ScimUiRefinementAndImports]`
     * FeatureFlag is cleaned up.
     * All parts dependent of this function should be replaced with
     * `ApplicationModel.isScimActive` state value
     * @returns Boolean
     */
    showScimDeactivateLink() {
      return this.isScimActive && this.ScimUiRefinementAndImports;
    },
  },

  methods: {
    ...mapActions(applicationModel, {
      activateScim: 'activateScim',
      addTranslationRule: 'addScimTranslationRule',
      configureScim: 'configureScim',
      deactivateScim: 'deactivateScim',
      removeTranslationRule: 'removeScimTranslationRule',
      resetGenericData: 'resetScimGenericData',
      resetCustomApiConfig: 'resetCustomApiConfig',
      setDefaultScimTranslationRules: 'setDefaultScimTranslationRules',
      setGenericOldData: 'setScimGenericOldData',
      setNeedsCredentials: 'setScimNeedsCredentials',
      setOldCustomApiConfig: 'setOldCustomApiConfig',
      setScimGroupsEnable: 'setScimGroupsEnable',
      setScimStatus: 'setScimStatus',
      setScimVersion: 'setScimScimVersion',
      setTestConnectionPassed: 'setScimTestConnectionPassed',
      setTokenKey: 'setScimTokenKey',
      setVanityUrl: 'setScimVanityUrl',
      updateTranslationRule: 'updateScimTranslationRule',
      updateConfigureScim: 'updateConfigureScim',
      verifyScimConnection: 'verifyScimConnection',
    }),

    async deactivate() {
      try {
        await this.deactivateScim();
        this.setDefaultScimTranslationRules();
        notify(success, 'Identity Management deactivated successfully.');
        this.emitScimEvent('scimInactive');
      } catch {
        notify(error, 'There was a problem deactivating Identity Management.');
      }

      ModalEventBus.close();
    },

    emitScimEvent(event, data) {
      SCIMPanelEventManager.$emit(event, data);
    },

    handleDeactivate() {
      ModalEventBus.open({
        component: 'ConfirmationModal',
        componentProps: {
          headerText: 'Deactivate Indentity Management Integration',
          bodyText: 'Are you sure? Deactivating Identity Management for this application means that JumpCloud will no longer create, update, or delete accounts for this application.',
          handleCancel: () => (ModalEventBus.close()),
          handleContinue: () => this.deactivate(),
          showClose: true,
          cancelText: 'cancel',
          continueText: 'confirm',
        },
      });
    },

    handleRedirectConfigure() {
      notify(success, 'Redirecting to SP login page');
      navigationTools.setLocation(this.redirectLocation);
    },

    isAppIdentityManagementExcluded(name) {
      return idmExcludedApps.includes(name);
    },
  },
};
